import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBan,
  FaBookOpen,
  FaCheckCircle,
  FaCrown,
  FaExclamationCircle,
  FaGraduationCap,
  FaIdCard,
  FaRedo,
  FaShieldAlt,
} from "react-icons/fa";

import { useAuth } from "../AuthContext";
import UnSubscribe from "../Subscription/UnSubscribe";

import "./ProfileStyles.css";

const BOOK_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;

const COURSE_LEVELS = ["A1", "A2", "B1", "B2"] as const;

const COURSE_NAMES: Record<string, string> = {
  A1: "A1 Deutschkurs",
  A2: "A2 Deutschkurs",
  B1: "B1 Deutschkurs",
  B2: "B2 Deutschkurs",
};

const WEBSITE_ENTITLEMENT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_ENTITLEMENT_ID?.trim() ||
  "website_full_access";

const WEBSITE_PRODUCT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_PACKAGE_ID?.trim() ||
  "website_full_month";

const MOBILE_PREMIUM_ENTITLEMENT =
  process.env.REACT_APP_REVENUECAT_MOBILE_PREMIUM_ENTITLEMENT_ID?.trim() ||
  "premium";

const normalizeEmail = (email?: string | null): string => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const hasExactValue = (values: string[], expected: string): boolean => {
  const cleanExpected = expected.trim().toLowerCase();

  return values.some(
    (value) =>
      String(value || "")
        .trim()
        .toLowerCase() === cleanExpected,
  );
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatLastChecked = (value: number): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const Profile: React.FC = () => {
  const {
    user,
    userId,
    userEmail,
    isLoggedIn,
    authLoading,

    accessChecked,
    accessLoading,
    accessError,
    accessLastCheckedAt,
    accessSource,
    hasAnyAccess,
    hasFullBookAccess,
    hasFullCourseAccess,
    accessibleBookLevels,
    accessibleCourseLevels,
    activeEntitlements,
    activeProducts,
    refreshAccess,
  } = useAuth();

  const [refreshing, setRefreshing] = useState<boolean>(false);

  const [showUnsubscribe, setShowUnsubscribe] = useState<boolean>(false);

  const cleanEmail = normalizeEmail(userEmail);

  const cleanUserId = String(userId || "").trim();

  const displayName =
    user?.displayName?.trim() || cleanEmail.split("@")[0] || "Lernender";

  const createdAt = formatDate(user?.metadata?.creationTime);

  const lastSignInAt = formatDate(user?.metadata?.lastSignInTime);

  const hasWebsitePremium = useMemo(() => {
    return (
      hasExactValue(activeEntitlements, WEBSITE_ENTITLEMENT) ||
      hasExactValue(activeProducts, WEBSITE_PRODUCT)
    );
  }, [activeEntitlements, activeProducts]);

  const hasMobilePremium = useMemo(() => {
    return hasExactValue(activeEntitlements, MOBILE_PREMIUM_ENTITLEMENT);
  }, [activeEntitlements]);

  const hasManualAccess = useMemo(() => {
    return accessSource.toLowerCase().includes("manual");
  }, [accessSource]);

  const unlockedBookCount = useMemo(() => {
    if (
      hasFullBookAccess ||
      accessibleBookLevels.length >= BOOK_LEVELS.length
    ) {
      return 60;
    }

    return accessibleBookLevels.length * 10;
  }, [accessibleBookLevels.length, hasFullBookAccess]);

  const unlockedCourseCount = useMemo(() => {
    if (
      hasFullCourseAccess ||
      accessibleCourseLevels.length >= COURSE_LEVELS.length
    ) {
      return COURSE_LEVELS.length;
    }

    return accessibleCourseLevels.length;
  }, [accessibleCourseLevels.length, hasFullCourseAccess]);

  const activeSubscriptions = useMemo(() => {
    const items: string[] = [];

    if (hasMobilePremium) {
      items.push("App-Premium aktiv");
    }

    if (hasWebsitePremium) {
      items.push("Website Premium aktiv");
    }

    if (hasManualAccess) {
      items.push("Manueller Zugang aktiv");
    }

    if (!hasManualAccess && accessibleCourseLevels.length > 0) {
      accessibleCourseLevels.forEach((level) => {
        items.push(COURSE_NAMES[level] || `${level} Deutschkurs`);
      });
    }

    if (items.length === 0 && hasFullBookAccess) {
      items.push("Bücher-Premium aktiv");
    }

    return Array.from(new Set(items));
  }, [
    accessibleCourseLevels,
    hasFullBookAccess,
    hasManualAccess,
    hasMobilePremium,
    hasWebsitePremium,
  ]);

  const premiumStatusText = useMemo(() => {
    if (hasFullBookAccess && hasFullCourseAccess) {
      return "Vollzugang aktiv";
    }

    if (hasMobilePremium) {
      return "App-Premium aktiv";
    }

    if (hasWebsitePremium) {
      return "Website Premium aktiv";
    }

    if (hasManualAccess) {
      return "Manueller Zugang aktiv";
    }

    if (hasFullBookAccess) {
      return "Bücherzugang aktiv";
    }

    if (accessibleCourseLevels.length > 0) {
      return "Kurszugang aktiv";
    }

    return "Kein aktiver Zugang erkannt";
  }, [
    accessibleCourseLevels.length,
    hasFullBookAccess,
    hasFullCourseAccess,
    hasManualAccess,
    hasMobilePremium,
    hasWebsitePremium,
  ]);

  const showInitialLoading = authLoading || (!accessChecked && accessLoading);

  const showBackgroundRefresh =
    !showInitialLoading && (accessLoading || refreshing);

  const handleRefresh = useCallback(async (): Promise<void> => {
    setRefreshing(true);

    try {
      await refreshAccess(true);
    } finally {
      setRefreshing(false);
    }
  }, [refreshAccess]);

  const handleSubscriptionChanged = useCallback(async (): Promise<void> => {
    setShowUnsubscribe(false);
    await handleRefresh();
  }, [handleRefresh]);

  useEffect(() => {
    document.documentElement.classList.add("profile-screen-page");

    document.body.classList.add("profile-screen-page");

    return () => {
      document.documentElement.classList.remove("profile-screen-page");

      document.body.classList.remove("profile-screen-page");
    };
  }, []);

  return (
    <main className="profile-page">
      <UnSubscribe
        isOpen={showUnsubscribe}
        onClose={() => setShowUnsubscribe(false)}
        userEmail={cleanEmail}
        userId={cleanUserId}
        subscriptionIds={[]}
        onPortalOpened={handleSubscriptionChanged}
        onCancelled={handleSubscriptionChanged}
      />

      <section className="profile-container">
        <header className="profile-header-card">
          <div className="profile-header-content">
            <p className="profile-kicker">
              <FaShieldAlt />
              Profil
            </p>

            <h1 className="profile-page-title">
              {isLoggedIn ? displayName : "Mein Konto"}
            </h1>

            <p className="profile-subtitle">
              Bücher und Kurse werden getrennt geprüft. Website Premium und
              App-Premium schalten alle Bücher frei, aber keine Deutschkurse.
            </p>
          </div>
        </header>

        {showInitialLoading && (
          <div className="profile-notice profile-notice-info">
            <FaRedo className="profile-spin" />

            <span>Konto und Zugang werden geprüft...</span>
          </div>
        )}

        {showBackgroundRefresh && (
          <div className="profile-notice profile-notice-info">
            <FaRedo className="profile-spin" />

            <span>Zugang wird im Hintergrund aktualisiert...</span>
          </div>
        )}

        {!authLoading && !isLoggedIn && (
          <div className="profile-notice profile-notice-warning">
            <FaExclamationCircle />

            <span>Bitte loggen Sie sich ein, um Ihr Profil zu sehen.</span>
          </div>
        )}

        {accessError && accessChecked && (
          <div className="profile-notice profile-notice-warning">
            <FaExclamationCircle />

            <span>{accessError}</span>
          </div>
        )}

        <div className="profile-dashboard-grid">
          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaIdCard />
              <span>Konto</span>
            </div>

            <div className="profile-info-list">
              <div>
                <span>Name</span>

                <strong>{displayName}</strong>
              </div>

              <div>
                <span>E-Mail</span>

                <strong>{cleanEmail || "—"}</strong>
              </div>

              <div>
                <span>Erstellt</span>

                <strong>{createdAt}</strong>
              </div>

              <div>
                <span>Letzte Anmeldung</span>

                <strong>{lastSignInAt}</strong>
              </div>

              <div>
                <span>Zugang geprüft</span>

                <strong>{formatLastChecked(accessLastCheckedAt)}</strong>
              </div>
            </div>
          </article>

          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaCrown />

              <span>Premium-Zugang</span>
            </div>

            <div
              className={`profile-status-pill ${
                hasAnyAccess
                  ? "profile-status-active"
                  : "profile-status-inactive"
              }`}
            >
              {hasAnyAccess ? <FaCheckCircle /> : <FaExclamationCircle />}

              <span>{premiumStatusText}</span>
            </div>

            <div className="profile-chip-list">
              {activeSubscriptions.length > 0 ? (
                activeSubscriptions.map((item) => (
                  <span key={item} className="profile-access-chip active">
                    {item}
                  </span>
                ))
              ) : (
                <span className="profile-access-chip">Kein Abo</span>
              )}
            </div>

            {hasWebsitePremium && (
              <button
                type="button"
                className="profile-unsubscribe-button"
                onClick={() => setShowUnsubscribe(true)}
              >
                <FaBan />

                <span>Website-Abo verwalten</span>
              </button>
            )}
          </article>

          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaBookOpen />

              <span>Bücherzugang</span>
            </div>

            <div className="profile-big-number">
              <strong>{unlockedBookCount}/60</strong>

              <span>freigeschaltete Bücher</span>
            </div>

            <div className="profile-chip-list">
              {accessibleBookLevels.length > 0 ? (
                accessibleBookLevels.map((level) => (
                  <span key={level} className="profile-access-chip active">
                    {level}
                  </span>
                ))
              ) : (
                <span className="profile-access-chip">Keine Bücher</span>
              )}
            </div>
          </article>

          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaGraduationCap />

              <span>Kurse</span>
            </div>

            <div className="profile-big-number">
              <strong>
                {unlockedCourseCount}/{COURSE_LEVELS.length}
              </strong>

              <span>freigeschaltete Kurse</span>
            </div>

            <div className="profile-chip-list">
              {accessibleCourseLevels.length > 0 ? (
                accessibleCourseLevels.map((level) => (
                  <span key={level} className="profile-access-chip active">
                    {COURSE_NAMES[level] || level}
                  </span>
                ))
              ) : (
                <>
                  <span className="profile-access-chip">Kein Kursabo</span>

                  <span className="profile-access-chip profile-access-chip-note">
                    Website Premium und App-Premium schalten nur Bücher frei
                  </span>
                </>
              )}
            </div>
          </article>

          {accessChecked && !accessLoading && !hasAnyAccess && (
            <article className="profile-panel profile-wide">
              <div className="profile-panel-head">
                <FaExclamationCircle />

                <span>Kein Zugang</span>
              </div>

              <div className="profile-notice profile-notice-warning">
                <FaExclamationCircle />

                <span>
                  Für diese E-Mail wurde kein Premium-Abo und kein manueller
                  Zugang gefunden.
                </span>
              </div>
            </article>
          )}
        </div>

        <div className="profile-actions">
          <button
            type="button"
            className="profile-secondary-button"
            onClick={() => void handleRefresh()}
            disabled={refreshing || accessLoading}
          >
            <FaRedo
              className={refreshing || accessLoading ? "profile-spin" : ""}
            />

            <span>Daten aktualisieren</span>
          </button>
        </div>
      </section>
    </main>
  );
};

export default Profile;
